































































import type { PropType } from '@nuxtjs/composition-api';
import { defineComponent, computed, ref } from '@nuxtjs/composition-api';
import { useVSFContext } from '@vue-storefront/core';
import { useI18n } from '~/composables/useI18n';
import type { ProductReview } from '~/types/product/attribute/ProductReview';
import ReviewsStars from '~/components/atoms/ReviewsStars.vue';
import ReviewAuthor from '~/components/atoms/Review/ReviewAuthor/ReviewAuthor.vue';
import { SCHEMA_ITEM_TYPE, SCHEMA_ITEM_PROP } from '~/constants/seo';
import { dateToYYYYMMDD } from '~/helpers/time/dateToYYYYMMDD/dateToYYYYMMDD';
import { EXPONDO_LOGO_DARK_SIMPLE_PATH } from '~/constants/expondoLogoPath';
import { getTimeAgo } from '~/helpers/time/getTimeAgo';
import { capitalizeString } from '~/helpers/capitalizeString';
import localizationDefinitions from '~/i18n/localizationDefinitions';
import { reviewGetters } from '~/utils/ct';

export default defineComponent({
  name: 'Review',
  components: {
    ReviewsStars,
    ReviewAuthor,
  },
  props: {
    review: {
      type: Object as PropType<ProductReview>,
      required: true,
    },
  },
  setup(props: {review: ProductReview}) {
    const { languageAndCountry: activeLocale } = useI18n();
    const relativeTime = computed(() => new Intl.RelativeTimeFormat(activeLocale.value, { style: 'long', numeric: 'auto' }));
    const date = computed(() => props?.review?.custom?.reviewCreatedAt?.[0]?.value || props?.review?.createdAt);
    const { time, unit } = getTimeAgo(date.value ? new Date(date.value) : undefined);
    const timeAgo = computed(() => time && unit ? relativeTime.value?.format(time, unit) : '');
    const { i18n } = useVSFContext();

    const source = computed(() => {
      const countryLocalization = localizationDefinitions.getByLanguageAndCountry(props.review.locale);
      const capitalizedWord = capitalizeString(props.review.custom?.reviewSource?.[0]?.value, '_');
      // eslint-disable-next-line @typescript-eslint/naming-convention
      return i18n.t('Posted on X', { _reviewSource_: capitalizedWord, _locale_: countryLocalization.label });
    });

    const isTranslatedReviewShowed = ref(true);
    const translationTogglerText = computed(() => isTranslatedReviewShowed.value ? i18n.t('Show original review') : i18n.t('Show translated review'));

    const originalTitle = computed(() => props.review?.title);
    const translatedTitle = computed(() => props.review.custom?.titleTranslations?.[0]?.value?.[activeLocale.value]);
    const originalMessage = computed(() => reviewGetters.getReviewMessage(props.review));
    const translatedMessage = computed(() => props.review.custom?.textTranslations?.[0]?.value?.[activeLocale.value]);
    const title = computed(() => isTranslatedReviewShowed.value && translatedTitle.value ? translatedTitle.value : originalTitle.value);
    const messageText = computed(() => isTranslatedReviewShowed.value && translatedMessage.value ? translatedMessage.value : originalMessage.value);
    const isReviewEmpty = computed(() => !originalMessage.value);
    const defaultTranslatedMessage = computed(() =>
      isTranslatedReviewShowed.value
        ? props.review.custom?.textTranslations?.[0]?.value?.[activeLocale.value]
        : props.review.custom?.textTranslations?.[0]?.value?.[props.review.locale],
    );
    const response = computed(() =>
      isTranslatedReviewShowed.value
        ? props.review.custom?.answerTranslations?.[0]?.value?.[activeLocale.value]
        : props.review.custom?.customerCareResponseText?.[0]?.value,
    );

    const message = computed(() => !isReviewEmpty.value ? messageText.value : defaultTranslatedMessage.value);

    const isReviewLocaleSameAsActiveLocale = computed(() => props.review.locale === activeLocale.value);
    const isWholeReviewTranslated = computed(() => translatedTitle.value && translatedMessage.value);
    const isDefaultReviewWithoutTitle = computed(() => !originalTitle.value && defaultTranslatedMessage.value);

    const showTranslateButton = computed(() => {
      return !isReviewLocaleSameAsActiveLocale.value && (isWholeReviewTranslated.value || isDefaultReviewWithoutTitle.value);
    });

    const triggerTranslation = () => { isTranslatedReviewShowed.value = !isTranslatedReviewShowed.value; };

    return {
      rating: reviewGetters.getReviewRating(props.review),
      author: reviewGetters.getReviewAuthor(props.review),
      message,
      isReviewEmpty,
      timeAgo,
      date,
      source,
      title,
      response,
      dateToYYYYMMDD,
      SCHEMA_ITEM_TYPE,
      SCHEMA_ITEM_PROP,
      EXPONDO_LOGO_DARK_SIMPLE_PATH,
      triggerTranslation,
      translationTogglerText,
      showTranslateButton,
    };
  },
});
